import { performLogout, registerBillingSessionStop } from './authLogout';
import * as Constants from './Constants';
import axios from 'axios';

/** Billing UI idle timeout — auto sign-out in the browser (30 minutes). */
export const BILLING_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const CHECK_INTERVAL_MS = 5 * 1000;
const HEARTBEAT_INTERVAL_MS = 20 * 1000;
const LAST_ACTIVITY_KEY = 'billingLastActivity';

let idleTimer = null;
let countdownTimer = null;
let heartbeatTimer = null;
let lastHeartbeatAt = 0;
let listenersBound = false;
let activeHistory = null;
let logoutInProgress = false;
const countdownListeners = new Set();

function touchBillingActivity() {
  if (!localStorage.getItem('UserId') || logoutInProgress) {
    return;
  }
  try {
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } catch (e) {
    // ignore
  }
  notifyCountdownListeners();
  maybeSendSessionHeartbeat(false);
}

function onUserActivity() {
  // If already past idle window, do not extend — force logout check first.
  if (isIdleExpired()) {
    checkIdleTimeout();
    return;
  }
  touchBillingActivity();
}

function onHashChange() {
  if (localStorage.getItem('UserId')) {
    onUserActivity();
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible' && localStorage.getItem('UserId')) {
    checkIdleTimeout();
  }
}

function bindActivityListeners() {
  if (listenersBound) {
    return;
  }
  listenersBound = true;
  // Intentionally omit mousemove/scroll — they keep sessions alive while the user is idle.
  ['click', 'keydown', 'mousedown', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, onUserActivity, { passive: true });
  });
  window.addEventListener('hashchange', onHashChange);
  document.addEventListener('visibilitychange', onVisibilityChange);
}

function unbindActivityListeners() {
  if (!listenersBound) {
    return;
  }
  listenersBound = false;
  ['click', 'keydown', 'mousedown', 'touchstart'].forEach((eventName) => {
    document.removeEventListener(eventName, onUserActivity);
  });
  window.removeEventListener('hashchange', onHashChange);
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

function notifyCountdownListeners() {
  const remaining = getBillingIdleRemainingMs();
  countdownListeners.forEach((listener) => {
    try {
      listener(remaining);
    } catch (e) {
      // ignore
    }
  });
}

function startCountdownTimer() {
  if (countdownTimer) {
    return;
  }
  countdownTimer = setInterval(notifyCountdownListeners, 1000);
}

function stopCountdownTimer() {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
  notifyCountdownListeners();
}

function getLastActivityMs() {
  try {
    return parseInt(sessionStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10) || 0;
  } catch (e) {
    return 0;
  }
}

function isIdleExpired() {
  const last = getLastActivityMs();
  if (!last) {
    return true;
  }
  return Date.now() - last >= BILLING_IDLE_TIMEOUT_MS;
}

function maybeSendSessionHeartbeat(force) {
  const userId = localStorage.getItem('UserId');
  if (!userId || logoutInProgress) {
    return;
  }
  const last = getLastActivityMs();
  if (!last || Date.now() - last >= BILLING_IDLE_TIMEOUT_MS) {
    return;
  }
  const now = Date.now();
  if (!force && now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) {
    return;
  }
  lastHeartbeatAt = now;
  axios
    .get(Constants.BACKEND_URL + '/user/session/heartbeat?userId=' + encodeURIComponent(userId))
    .catch(function (err) {
      const status = err && err.response && err.response.status;
      // Java session gone / idle-expired server-side → force full sign-out.
      if (status === 401) {
        forceIdleLogout('session-revoked');
      }
    });
}

function sendSessionHeartbeat() {
  maybeSendSessionHeartbeat(true);
}

function startHeartbeatTimer() {
  if (heartbeatTimer) {
    return;
  }
  sendSessionHeartbeat();
  heartbeatTimer = setInterval(sendSessionHeartbeat, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeatTimer() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  lastHeartbeatAt = 0;
}

export function getBillingIdleRemainingMs() {
  if (!localStorage.getItem('UserId')) {
    return 0;
  }
  const last = getLastActivityMs();
  if (!last) {
    return BILLING_IDLE_TIMEOUT_MS;
  }
  return Math.max(0, BILLING_IDLE_TIMEOUT_MS - (Date.now() - last));
}

export function subscribeBillingIdleCountdown(listener) {
  countdownListeners.add(listener);
  listener(getBillingIdleRemainingMs());
  return function unsubscribe() {
    countdownListeners.delete(listener);
  };
}

function forceIdleLogout(reason) {
  if (logoutInProgress) {
    return;
  }
  logoutInProgress = true;
  const history = activeHistory;
  // Keep timer identity until performLogout clears auth; avoid leaving a
  // "timer stopped but still logged in" gap if logout throws.
  try {
    performLogout(history, { reason: reason || 'idle-timeout' });
  } catch (e) {
    logoutInProgress = false;
    // Last resort hard redirect if logout path throws.
    try {
      localStorage.removeItem('UserId');
      window.location.replace(
        window.location.origin + '/?idle_logout=' + Date.now() + '#/user-pages/login-1'
      );
    } catch (e2) {
      // ignore
    }
  }
}

function checkIdleTimeout() {
  if (logoutInProgress) {
    return;
  }
  const userId = localStorage.getItem('UserId');
  if (!userId) {
    stopBillingSessionWatch();
    return;
  }

  if (isIdleExpired()) {
    forceIdleLogout('idle-timeout');
  } else {
    notifyCountdownListeners();
  }
}

export function recordBillingActivity() {
  onUserActivity();
}

export function initBillingSessionOnLogin() {
  logoutInProgress = false;
  touchBillingActivity();
  maybeSendSessionHeartbeat(true);
}

export function startBillingSessionWatch(history) {
  if (!localStorage.getItem('UserId') || logoutInProgress) {
    return;
  }
  activeHistory = history;
  if (!idleTimer) {
    // Do not reset activity if we already have a stamp (e.g. route remount).
    if (!getLastActivityMs()) {
      touchBillingActivity();
    }
    idleTimer = setInterval(checkIdleTimeout, CHECK_INTERVAL_MS);
  }
  bindActivityListeners();
  startCountdownTimer();
  startHeartbeatTimer();
  // Immediate check in case the tab slept past the idle window.
  checkIdleTimeout();
}

export function stopBillingSessionWatch() {
  activeHistory = null;
  if (idleTimer) {
    clearInterval(idleTimer);
    idleTimer = null;
  }
  stopCountdownTimer();
  stopHeartbeatTimer();
  unbindActivityListeners();
}

registerBillingSessionStop(stopBillingSessionWatch);
